import React, { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { MessageSquare, Send, Mail, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function MessagesPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [form, setForm] = useState({ to_user_id: "", subject: "", content: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/messages").then(({ data }) => setItems(data));
    api.get("/users").then(({ data }) => setRecipients(data)).catch(() => {});
  }, []);

  const send = async () => {
    if (!form.to_user_id || !form.subject || !form.content) return toast.error("Tous les champs sont requis");
    setLoading(true);
    try {
      await api.post("/messages", form);
      toast.success("Message envoyé");
      setOpen(false);
      setForm({ to_user_id: "", subject: "", content: "" });
      api.get("/messages").then(({ data }) => setItems(data));
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-5" data-testid="page-messages">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-heading text-3xl font-extrabold text-[#1E3A8A] tracking-tight">Messagerie</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-[#EA580C] hover:bg-orange-700 text-white gap-2" data-testid="new-message-btn"><Send className="h-4 w-4" /> Nouveau message</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Envoyer un message</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Destinataire *</Label>
                <Select value={form.to_user_id} onValueChange={(v) => setForm({...form, to_user_id: v})}>
                  <SelectTrigger data-testid="msg-to"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent>
                    {recipients.map(r => <SelectItem key={r.id} value={r.id}>{r.full_name} ({r.role})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Sujet *</Label><Input value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} data-testid="msg-subject" /></div>
              <div><Label>Message *</Label><Textarea rows={5} value={form.content} onChange={(e) => setForm({...form, content: e.target.value})} data-testid="msg-content" /></div>
              <Button onClick={send} disabled={loading} className="bg-[#166534] text-white w-full" data-testid="msg-send">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Envoyer"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="space-y-3">
        {items.map((m) => (
          <div key={m.id} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`msg-${m.id}`}>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-[#1E3A8A] text-white flex items-center justify-center flex-shrink-0">
                <Mail className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-sm font-semibold text-[#1E3A8A]">{m.from_id === user.id ? "Vous" : m.from_name}</div>
                  <div className="text-xs text-slate-500">{fmtDate(m.created_at)}</div>
                </div>
                <div className="font-heading font-bold text-base text-[#0F172A] mb-1">{m.subject}</div>
                <p className="text-sm text-slate-700 leading-relaxed">{m.content}</p>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-center text-slate-500 py-12 bg-white rounded-xl border border-slate-200">Aucun message</div>}
      </div>
    </div>
  );
}
